import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message, Spin } from 'antd';
import useCurrentUser from 'hooks/useCurrentUser';
import { PATH } from 'constants/path';
import { isGroupAdmin } from 'utils/common';
import { TScopedProviderOption } from 'types/group';
import useCredentialProviders from 'hooks/credentialing/useCredentialProviders';
import useScopedProviderOptions from 'hooks/credentialing/useScopedProviderOptions';
import { useGroupCredentialingEnrollment } from 'hooks/credentialing/useGroupCredentialingEnrollment';
import useCheckLeadFormByEmail from 'hooks/ppa/useCheckLeadFormByEmail';
import { isCredentialingHelpType } from 'pages/PPALeadFormPage/helpType';
import SelectProvidersStep from './components/SelectProvidersStep';
import PracticeSetupStep from './components/PracticeSetupStep';
import AddProvidersStep from './components/AddProvidersStep';
import FlowShell from './components/FlowShell';
import { npiIssueText, type TNpiIssue } from 'utils/npiCheck';
import { decodeEnrollmentEntityType, encodeEnrollmentEntityType } from './constants';
import { addGroupCredentialingProvidersByIds, saveGroupCredentialingEnrollment } from 'api/credentialing';
import { TCredentialingProvider, PracticeEnrollment } from 'types/credentialing';

enum EStep {
    SELECT_PROVIDERS,
    PRACTICE_SETUP,
    ADD_PROVIDERS,
}

// Deep-link support: `?step=add_providers` jumps straight to a step.
const STEP_QUERY_MAP: Record<string, EStep> = {
    select_providers: EStep.SELECT_PROVIDERS,
    practice_setup: EStep.PRACTICE_SETUP,
    add_providers: EStep.ADD_PROVIDERS,
};

// Reverse map: each step carries its slug in the URL so a refresh / browser back lands on the right step.
const STEP_TO_QUERY: Record<EStep, string> = {
    [EStep.SELECT_PROVIDERS]: 'select_providers',
    [EStep.PRACTICE_SETUP]: 'practice_setup',
    [EStep.ADD_PROVIDERS]: 'add_providers',
};

const GroupCredentialingPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentUser] = useCurrentUser();

    // Fetch the providers a team owner can pick to credential; the select step only exists when some exist.
    const [providerOptions, providerOptionsLoading] = useScopedProviderOptions();

    // Team owners/admins get the provider-select pre-step first, but only when there are providers to pick.
    // Everyone else (and owners with no pickable providers) starts at setup.
    const isGroup = useMemo(() => isGroupAdmin(currentUser), [currentUser]);
    const selectStepAvailable = isGroup && providerOptions.length > 0;

    // A `?step=` query param deep-links to a specific step and overrides the isGroup default.
    const queryStep = useMemo(() => {
        const raw = searchParams.get('step');
        return raw ? STEP_QUERY_MAP[raw.toLowerCase()] : undefined;
    }, [searchParams]);

    // Fetch real data from backend
    const [enrollmentData, enrollmentLoading, refetchCredentialingEnrollment] = useGroupCredentialingEnrollment();
    const [providersData, providersLoading, refetchCredentialProviders] = useCredentialProviders();
    const [leadFormData] = useCheckLeadFormByEmail(currentUser?.user?.email || '');

    const [step, setStep] = useState<EStep>(queryStep ?? EStep.PRACTICE_SETUP);
    const [roster, setRoster] = useState<TCredentialingProvider[]>([]);
    const [enrollment, setEnrollment] = useState<PracticeEnrollment>({
        legalBusinessName: '',
        groupNpi: '',
        taxId: '',
        entityType: '',
        entityTypeOther: '',
        practiceLocation: '',
        w9FileName: '',
    });
    const [submitLoading, setSubmitLoading] = useState(false);
    // Group NPI 的 NPI Registry 校验结果（由 PracticeSetupStep 上报）：Next 据此拦截。
    // Save and exit 不拦——存草稿允许半填，与「部分填写可保存续填」的口径一致
    const [npiState, setNpiState] = useState<TNpiIssue>(null);

    // Initialize from fetched data
    React.useEffect(() => {
        if (enrollmentData) {
            // /group/enrollment 整份覆盖：保留 detail 的全部字段（w9FileUrl / practiceRequirements 等），
            // 只覆盖表单要规整的几项，Next / Save and exit 提交时不会把没展示的字段冲掉
            setEnrollment({
                ...enrollmentData,
                legalBusinessName: enrollmentData.legalBusinessName || '',
                groupNpi: enrollmentData.groupNpi || '',
                taxId: enrollmentData.taxId || '',
                ...decodeEnrollmentEntityType(enrollmentData.entityType),
                practiceLocation: enrollmentData.practiceLocation || '',
                w9FileName: enrollmentData.w9FileName || '',
            });
        }
    }, [enrollmentData]);

    React.useEffect(() => {
        if (providersData) {
            setRoster(providersData);
        }
    }, [providersData]);

    // Keep the initial step in sync once currentUser and provider options resolve (both unknown on
    // first render). A `?step=` deep-link takes precedence, but the select step is skipped when it
    // isn't available (not a group owner, or no providers to pick).
    React.useEffect(() => {
        if (providerOptionsLoading) return;
        if (queryStep !== undefined) {
            setStep(queryStep === EStep.SELECT_PROVIDERS && !selectStepAvailable ? EStep.PRACTICE_SETUP : queryStep);
            return;
        }
        setStep(selectStepAvailable ? EStep.SELECT_PROVIDERS : EStep.PRACTICE_SETUP);
    }, [selectStepAvailable, queryStep, providerOptionsLoading]);

    // Move to a step by pushing its slug into the URL; the queryStep effect above picks up the
    // new `?step=` and syncs local step state. Used by both Next and Back.
    const goToStep = (next: EStep) => {
        setSearchParams({ step: STEP_TO_QUERY[next] });
    };

    const isCredentialingUser = isCredentialingHelpType(leadFormData?.leadForm?.practiceHelpType);
    const credentialingCompleted = roster.some((p) => p.onboardingStatus === 'COMPLETED');
    const exitFlow = () => {
        if (isCredentialingUser && !credentialingCompleted) {
            navigate(PATH.CREDENTIALING_LANDING, { state: { savedExit: true } });
        } else {
            navigate(`${PATH.DASHBOARD}/${PATH.CREDENTIALING}`);
        }
    };

    const handleExit = async () => {
        if (step === EStep.PRACTICE_SETUP) {
            setSubmitLoading(true);
            try {
                const res = await saveGroupCredentialingEnrollment(encodeEnrollmentEntityType(enrollment));
                if (res && !res.error) {
                    exitFlow();
                } else {
                    message.error(res?.error || 'Failed to save practice setup');
                }
            } finally {
                setSubmitLoading(false);
            }
        } else {
            // ADD_PROVIDERS or SELECT_PROVIDERS: direct exit
            exitFlow();
        }
    };

    // Back 走显式的步骤关系，不依赖 history：无论从入口、landing 续填还是直接刷新进来，
    // 最终都能一步步退回入口的计费 NPI 选择页。
    // `repick=1` 让 start 页跳过续填判定停在入口，并把此后的重选按「切换」处理（switch-billing-type）。
    const handleBack = () => {
        if (step === EStep.ADD_PROVIDERS) {
            goToStep(EStep.PRACTICE_SETUP);
            return;
        }
        // 有 Select providers 前置步骤时先退回前置步骤，再按一次 Back 才回入口
        if (step === EStep.PRACTICE_SETUP && selectStepAvailable) {
            goToStep(EStep.SELECT_PROVIDERS);
            return;
        }
        navigate(`${PATH.CREDENTIALING_START}?repick=1`, { replace: true });
    };

    const handleProvidersSelected = async (selected: TScopedProviderOption[]) => {
        const res = await addGroupCredentialingProvidersByIds(selected.map((p) => p.providerId));
        if (res && !res.error) {
            refetchCredentialProviders();
            goToStep(EStep.PRACTICE_SETUP);
        } else {
            message.error(res?.error || 'Failed to add providers');
        }
    };

    const handleNextPracticeSetup = async () => {
        // Group NPI 填了就必须在 NPI Registry 查得到且是机构 NPI（Type 2）；查询中同样拦住。
        // 错误文案 PracticeEnrollmentFields 里的 NpiRegistryBox 已内联展示，这里只 toast
        if (npiState) {
            message.error(npiIssueText(npiState, 'Type 2'));
            return;
        }
        setSubmitLoading(true);
        try {
            const res = await saveGroupCredentialingEnrollment(encodeEnrollmentEntityType(enrollment));
            if (res && !res.error) {
                refetchCredentialingEnrollment();
                goToStep(EStep.ADD_PROVIDERS);
            } else {
                message.error(res?.error || 'Failed to save practice setup');
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleContinueToReview = () => {
        navigate(`${PATH.CREDENTIALING_START}?mode=group-review`);
    };

    const isDone = (p: TCredentialingProvider) =>
        p.onboardingStatus === 'COMPLETED' ||
        (p.onboardingStatus === 'IN_PROGRESS' && p.onboardingStep === 'PAYERS_SELECTED');
    const reviewDisabled =
        roster.length === 0 ||
        roster.some((p) => !isDone(p)) ||
        roster.every((p) => p.onboardingStatus === 'COMPLETED');

    // SELECT_PROVIDERS renders its own Continue button (selection-count logic); no footer primary.
    let primary: { label: string; disabled: boolean; onClick: () => void } | undefined;
    if (step === EStep.PRACTICE_SETUP) {
        primary = { label: 'Next', disabled: submitLoading, onClick: handleNextPracticeSetup };
    } else if (step === EStep.ADD_PROVIDERS) {
        primary = { label: 'Continue to review', disabled: reviewDisabled, onClick: handleContinueToReview };
    }

    const pageLoading = enrollmentLoading || providersLoading || providerOptionsLoading;

    return (
        <FlowShell
            onExit={handleExit}
            onBack={handleBack}
            primaryLabel={primary?.label}
            primaryDisabled={primary?.disabled}
            onPrimary={primary?.onClick}
        >
            <Spin spinning={pageLoading}>
                {step === EStep.SELECT_PROVIDERS && (
                    <SelectProvidersStep
                        providers={providerOptions}
                        loading={providerOptionsLoading}
                        roster={roster}
                        onContinue={handleProvidersSelected}
                    />
                )}
                {step === EStep.PRACTICE_SETUP && (
                    <PracticeSetupStep
                        enrollment={enrollment}
                        onEnrollmentChange={setEnrollment}
                        roster={roster}
                        initialPayers={enrollmentData?.payers || []}
                        onNpiIssueChange={setNpiState}
                    />
                )}
                {step === EStep.ADD_PROVIDERS && (
                    <AddProvidersStep
                        title="Add your providers"
                        subtitle="Tell us if you also see patients, then add each provider. Providers inherit the practice payers. Step 2 of 2."
                        roster={roster}
                        onRefetch={refetchCredentialProviders}
                        showOwnerQuestion
                    />
                )}
            </Spin>
        </FlowShell>
    );
};

export default GroupCredentialingPage;
